import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { MapPin, Home } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { formatMarkdownToSafeHtml } from "@/lib/sanitize";
import { logger } from "@/lib/logger";

interface NorthPropertyData {
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  price: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
}

interface CompsPreviewProps {
  northId: string;
  compsSummary: string | null;
  forceMobileLayout?: boolean;
  isDemo?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  northProperty?: NorthPropertyData | null;
}

interface Comp {
  id: string;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  status: string | null;
  image_url: string | null;
  zillow_url: string | null;
}

export const CompsPreview = ({ northId, compsSummary, forceMobileLayout = false, isDemo = false, primaryColor, secondaryColor, northProperty: northPropertyProp }: CompsPreviewProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapboxToken, setMapboxToken] = useState("");
  const [webglError, setWebglError] = useState(false);
  const queryClient = useQueryClient();

  // Fetch Mapbox token - check cache first, then fetch if needed
  useEffect(() => {
    const fetchToken = async () => {
      try {
        // First check if token is already cached (preloaded by PublicNorthReport)
        const cachedToken = queryClient.getQueryData<string>(["mapbox-token", "public"]);
        if (cachedToken) {
          setMapboxToken(cachedToken);
          return;
        }

        // Check if user is authenticated before fetching token
        const { data: { session } } = await supabase.auth.getSession();

        // For demo mode, we can fetch token without auth
        // For non-demo, non-authenticated users, try public token fetch
        if (!session && !isDemo) {
          // Try to fetch public token (preloaded by PublicNorthReport)
          const { data, error } = await supabase.functions.invoke('get-mapbox-token', {
            body: { public: true }
          });
          if (!error && data?.token) {
            setMapboxToken(data.token);
            // Cache it for future use
            queryClient.setQueryData(["mapbox-token", "public"], data.token);
          }
          return;
        }

        // If demo mode and no session, pass demo flag to edge function
        const invokeOptions = !session && isDemo
          ? { body: { demo: true } }
          : {};

        const { data, error } = await supabase.functions.invoke('get-mapbox-token', invokeOptions);
        if (error) throw error;
        if (data?.token) {
          setMapboxToken(data.token);
          // Cache it
          queryClient.setQueryData(["mapbox-token", "public"], data.token);
        }
      } catch (error) {
        logger.error('Error fetching Mapbox token:', error);
      }
    };
    fetchToken();
  }, [isDemo, queryClient]);

  const { data: comps } = useQuery({
    queryKey: ['comps-preview', northId],
    queryFn: async () => {
      // Check if this is a demo report
      const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      
      if (isDemo) {
        // For demo, only fetch from demo table
        const { data: demoComps } = await supabase
          .from('demo_comparable_properties')
          .select('*')
          .eq('demo_report_id', northId);
        
        return (demoComps || []).map(comp => ({
          id: comp.id,
          address: comp.address,
          city: comp.city,
          state: comp.state,
          zip: comp.zip,
          price: comp.price,
          beds: comp.bedrooms,
          baths: comp.bathrooms,
          sqft: comp.square_feet,
          status: comp.status,
          image_url: comp.image_url,
          zillow_url: comp.zillow_url,
        })) as Comp[];
      }

      // Otherwise fetch from regular table - only get visible comps
      const { data, error } = await supabase
        .from('comparable_properties')
        .select('*')
        .eq('north_id', northId)
        .eq('visible', true)  // Only show visible comps
        .order('price', { ascending: false })
        .limit(6);

      if (error) throw error;

      // Map database columns to Comp interface
      return (data || []).map(comp => ({
        id: comp.id,
        address: comp.address,
        city: comp.city,
        state: comp.state,
        zip: comp.zip,
        price: comp.price,
        beds: comp.bedrooms,
        baths: comp.bathrooms,
        sqft: comp.square_feet,
        status: comp.status,
        image_url: comp.image_url,
        zillow_url: comp.zillow_url,
      })) as Comp[];
    },
  });

  // Fetch the north property data for the subject property marker.
  // If northPropertyProp is provided (e.g. from PublicNorthReport via SECURITY DEFINER RPC),
  // skip the query — anon users cannot read norths directly.
  const { data: northPropertyQueried } = useQuery({
    queryKey: ['north-property', northId],
    queryFn: async () => {
      const isDemoReport = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
      if (isDemoReport) {
        const { data: demoReport } = await supabase
          .from('demo_reports')
          .select('address, city, state, zip, price, bedrooms, bathrooms, square_feet')
          .eq('id', northId)
          .maybeSingle();
        return demoReport ?? null;
      }
      const { data } = await supabase
        .from('norths')
        .select('address, city, state, zip, price, bedrooms, bathrooms, square_feet')
        .eq('id', northId)
        .maybeSingle();
      return data ?? null;
    },
    enabled: !northPropertyProp,
  });

  const northProperty = northPropertyProp ?? northPropertyQueried ?? null;

  // No filtering on public report - only show visible comps (already filtered in query)

  const formatPrice = (price: number | null) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatStatus = (status: string | null) => {
    if (!status) return 'Listed for sale';
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusBadgeColor = (status: string | null): string => {
    if (!status) return '#374151'; // Gray-700 for unknown
    const statusLower = status.toLowerCase();
    
    // Green for sold properties
    if (statusLower.includes('sold') || statusLower === 'sold') {
      return '#15803d'; // Green-700
    }
    
    // Blue for for sale/active listings
    if (statusLower.includes('sale') || statusLower.includes('active') || statusLower === 'for_sale') {
      return '#1d4ed8'; // Blue-700
    }
    
    // Orange/amber for pending
    if (statusLower.includes('pending')) {
      return '#b45309'; // Amber-700
    }
    
    // Default gray for other statuses
    return '#374151'; // Gray-700
  };

  // Initialize map with markers
  useEffect(() => {
    if (!mapContainer.current || !mapboxToken || !comps || comps.length === 0 || !northProperty) return;
    if (webglError) return; // Don't attempt to initialize if WebGL already failed

    // Check for WebGL support before initializing
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
    if (!gl) {
      logger.error('WebGL is not supported in this browser');
      setWebglError(true);
      return;
    }

    mapboxgl.accessToken = mapboxToken;

    // Calculate bounds to fit all properties
    const bounds = new mapboxgl.LngLatBounds();
    
    try {
      // Center map on first property's approximate location (Kentucky/Ohio area)
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-84.5, 39.0],
        zoom: 9,
        failIfMajorPerformanceCaveat: false, // Don't fail on performance issues
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Handle map errors (set up after map is created)
      map.current.on('error', (e) => {
        logger.error('Map error:', e);
        if (e.error?.message?.includes('WebGL')) {
          setWebglError(true);
        }
      });
    } catch (error) {
      logger.error('Failed to initialize Mapbox map (likely WebGL issue):', error);
      setWebglError(true);
      return;
    }

    // Use Mapbox Geocoding API to get coordinates and add markers
    map.current.on('load', async () => {
      // First, add the subject property marker (North property) with home icon
      const subjectAddress = `${northProperty.address}, ${northProperty.city}, ${northProperty.state} ${northProperty.zip}`;
      
      try {
        let coordinates;
        
        if (isDemo) {
          // For demo, place home icon at random location in 90210
          // Beverly Hills 90210 center: ~34.0901° N, -118.4065° W
          // Add random offset (roughly ±0.02 degrees for variety within the area)
          const randomLat = 34.0901 + (Math.random() - 0.5) * 0.04;
          const randomLng = -118.4065 + (Math.random() - 0.5) * 0.04;
          coordinates = [randomLng, randomLat];
        } else {
          // Normal geocoding for real addresses
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(subjectAddress)}.json?access_token=${mapboxToken}&limit=1`
          );
          const data = await response.json();
          coordinates = data.features?.[0]?.center;
        }
        
        if (coordinates) {
          const [lng, lat] = coordinates;
          bounds.extend([lng, lat]);

          // Create a home icon marker for subject property
          const el = document.createElement('div');
          el.style.width = '32px';
          el.style.height = '32px';
          el.style.backgroundColor = 'white';
          el.style.borderRadius = '50%';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';
          el.style.cursor = 'pointer';
          el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
          el.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0c0f24" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          `;

          new mapboxgl.Marker(el)
            .setLngLat([lng, lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div style="padding: 8px;">
                  <p style="font-weight: 700; margin-bottom: 4px; color: #0c0f24;">Subject Property</p>
                  <p style="font-weight: 600; margin-bottom: 4px;">${formatPrice(northProperty.price)}</p>
                  <p style="font-size: 12px; margin-bottom: 2px;">${northProperty.address}</p>
                  <p style="font-size: 11px; color: #666;">${northProperty.beds} bed • ${northProperty.baths} bath • ${northProperty.sqft?.toLocaleString()} sqft</p>
                </div>`
              )
            )
            .addTo(map.current!);
        }
      } catch (error) {
        logger.error('Error geocoding subject property:', error);
      }

      // Then add comparable properties with smaller dark dots
      for (let i = 0; i < comps.length; i++) {
        const comp = comps[i];
        const address = `${comp.address}, ${comp.city}, ${comp.state} ${comp.zip}`;
        
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxToken}&limit=1`
          );
          const data = await response.json();
          
          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            bounds.extend([lng, lat]);

            // Create a small dark dot marker for comps
            const el = document.createElement('div');
            el.style.width = '16px';
            el.style.height = '16px';
            el.style.backgroundColor = '#0c0f24';
            el.style.borderRadius = '50%';
            el.style.cursor = 'pointer';
            el.style.border = '2px solid white';
            el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

            new mapboxgl.Marker(el)
              .setLngLat([lng, lat])
              .setPopup(
                new mapboxgl.Popup({ offset: 15 }).setHTML(
                  `<div style="padding: 8px;">
                    <p style="font-weight: 600; margin-bottom: 4px;">${formatPrice(comp.price)}</p>
                    <p style="font-size: 12px; margin-bottom: 2px;">${comp.address}</p>
                    <p style="font-size: 11px; color: #666;">${comp.beds} bed • ${comp.baths} bath • ${comp.sqft?.toLocaleString()} sqft</p>
                  </div>`
                )
              )
              .addTo(map.current!);
          }
        } catch (error) {
          logger.error('Error geocoding address:', error);
        }
      }

      // Fit map to show all markers
      if (!bounds.isEmpty()) {
        map.current?.fitBounds(bounds, { padding: 50, maxZoom: 12 });
      }
    });

    return () => {
      map.current?.remove();
    };
  }, [mapboxToken, comps, northProperty, webglError]);

  const handleSetToken = async () => {
    // No longer needed - token is fetched automatically
  };

  if (!comps || comps.length === 0) {
    return null;
  }

  return (
    <div>
      <div>
        <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">HOW YOU COMPARE</h3>
        
        {compsSummary && (
          <div className="mb-6">
            <div
              className="text-foreground/90 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: formatMarkdownToSafeHtml(compsSummary)
              }}
            />
          </div>
        )}
      </div>

      <div className={forceMobileLayout ? "flex flex-col gap-6" : "flex flex-col md:flex-row gap-6"}>
        {/* Interactive Map - Show first on mobile */}
        <div className={forceMobileLayout ? "flex-shrink-0 h-[300px] w-full order-first" : "flex-shrink-0 h-[300px] md:h-auto md:w-[400px] order-last"}>
          {!mapboxToken ? (
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden border border-border relative flex items-center justify-center">
              <div className="text-center space-y-2">
                <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  Loading map...
                </p>
              </div>
            </div>
          ) : webglError ? (
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden border border-border relative flex items-center justify-center">
              <div className="text-center space-y-2 px-4">
                <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  Map unavailable
                </p>
                <p className="text-xs text-muted-foreground/70">
                  Your browser doesn't support interactive maps
                </p>
              </div>
            </div>
          ) : (
            <div ref={mapContainer} className="w-full h-full rounded-lg overflow-hidden border border-border" />
          )}
        </div>

        {/* Comparable Properties List - Single Column, Compact */}
        <div className="flex-1 space-y-3">
          {comps.map((comp) => (
            <div key={comp.id} className="flex gap-3 p-2 border border-border rounded hover:shadow-sm transition-all bg-card">
              {/* Property Image - Small Thumbnail */}
              {comp.image_url ? (
                <img
                  src={comp.image_url}
                  alt={comp.address}
                  className="w-20 h-16 object-cover rounded flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              ) : (
                <div className="w-20 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                </div>
              )}

              {/* Property Details - Very Compact */}
              <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                <div>
                  <div className="font-semibold text-sm mb-0.5">{formatPrice(comp.price)}</div>

                  <h3 className="font-medium text-xs text-foreground leading-tight">
                    {comp.address}
                  </h3>

                  <p className="text-[10px] text-muted-foreground leading-tight">
                    {comp.city}, {comp.state} {comp.zip}
                  </p>
                </div>
              </div>

              {/* Right side - Status and Button */}
              <div className="flex-shrink-0 self-center flex flex-col items-end gap-1.5">
                <div
                  className="inline-flex items-center rounded-full border border-transparent px-1.5 py-0 h-4 text-[10px] font-semibold"
                  style={{
                    backgroundColor: getStatusBadgeColor(comp.status),
                    color: 'white'
                  }}
                >
                  {formatStatus(comp.status)}
                </div>
                
                {comp.zillow_url && (
                  isDemo ? (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-muted-foreground rounded text-[10px] font-medium cursor-not-allowed opacity-60">
                      <ZillowIcon className="h-3 w-3" />
                      <span>View Listing</span>
                    </div>
                  ) : (
                    <a
                      href={comp.zillow_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded text-[10px] font-medium transition-colors"
                    >
                      <ZillowIcon className="h-3 w-3" />
                      <span>View Listing</span>
                    </a>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
